import { Component, Input } from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { ThemeSection } from '../services/theme.service';
import { BadgeVariant, BadgeSize, BadgePosition } from '../models/badge.model';

const badgeVariants = cva({
  base: [
    'inline-flex items-center justify-center font-medium rounded-full',
    'backdrop-blur-md border transition-all duration-300 ease-out transform-gpu',
    'hover:scale-110 active:scale-95',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-70 before:rounded-full before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-gray-600/90 text-white border-gray-500/50',
        'shadow-lg shadow-gray-500/30',
        'dark:bg-gray-500/90 dark:border-gray-400/50',
      ].join(' '),
      primary: [
        'bg-primary-600/90 text-white border-primary-500/50',
        'shadow-lg shadow-primary-500/40',
        'dark:bg-primary-500/90 dark:border-primary-400/50',
      ].join(' '),
      secondary: [
        'bg-gray-500/90 text-white border-gray-400/50',
        'shadow-lg shadow-gray-500/30',
        'dark:bg-gray-400/90 dark:border-gray-300/50',
      ].join(' '),
      success: [
        'bg-cyan-600/90 text-white border-cyan-500/50',
        'shadow-lg shadow-cyan-500/40',
        'dark:bg-cyan-500/90 dark:border-cyan-400/50',
      ].join(' '),
      warning: [
        'bg-yellow-600/90 text-white border-yellow-500/50',
        'shadow-lg shadow-yellow-500/40',
        'dark:bg-yellow-500/90 dark:border-yellow-400/50',
      ].join(' '),
      error: [
        'bg-red-600/90 text-white border-red-500/50',
        'shadow-lg shadow-red-500/40',
        'dark:bg-red-500/90 dark:border-red-400/50',
      ].join(' '),
      info: [
        'bg-blue-600/90 text-white border-blue-500/50',
        'shadow-lg shadow-blue-500/40',
        'dark:bg-blue-500/90 dark:border-blue-400/50',
      ].join(' '),
      premium: [
        'bg-linear-to-r from-primary-600/95 to-primary-700/95 text-white border-primary-500/60',
        'shadow-xl shadow-primary-500/50',
        'dark:from-primary-500/95 dark:to-primary-600/95 dark:border-primary-400/60',
      ].join(' '),
      danger: [
        'bg-red-600/90 text-white border-red-500/50',
        'shadow-lg shadow-red-500/40',
        'dark:bg-red-500/90 dark:border-red-400/50',
      ].join(' '),
    },
    size: {
      xs: 'h-4 min-w-4 px-1 text-xs',
      sm: 'h-5 min-w-5 px-1.5 text-xs',
      md: 'h-6 min-w-6 px-2 text-sm',
      lg: 'h-8 min-w-8 px-3 text-base',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'sm',
  },
});

const containerVariants = cva({
  base: 'relative inline-block',
  variants: {
    position: {
      'top-right': '[&_.badge]:absolute [&_.badge]:-top-1 [&_.badge]:-right-1',
      'top-left': '[&_.badge]:absolute [&_.badge]:-top-1 [&_.badge]:-left-1',
      'bottom-right': '[&_.badge]:absolute [&_.badge]:-bottom-1 [&_.badge]:-right-1',
      'bottom-left': '[&_.badge]:absolute [&_.badge]:-bottom-1 [&_.badge]:-left-1',
    },
  },
  defaultVariants: {
    position: 'top-right',
  },
});

@Component({
  selector: 'ui-badge-glass',
  standalone: true,
  imports: [],
  templateUrl: './badge-glass.component.html',
})
export class BadgeGlassComponent {
  @Input() content: string | number | undefined;
  @Input() variant: BadgeVariant = 'default';
  @Input() size: BadgeSize = 'sm';
  @Input() position: BadgePosition = 'top-right';
  @Input() max: number = 99;
  @Input() showZero: boolean = false;
  @Input() standalone: boolean = false;
  @Input() hidden: boolean = false;
  @Input() pulse: boolean = false;
  @Input() ariaLabel: string = '';
  @Input() themeSection: ThemeSection = 'global';

  // Return computed badge classes based on current inputs.
  public computedBadgeClasses(): string {
    return badgeVariants({
      variant: this.variant,
      size: this.size,
    });
  }

  // Return computed container classes based on current inputs.
  public computedContainerClasses(): string {
    return containerVariants({
      position: this.position,
    });
  }

  // Compute display content from inputs immediately (getter used by template/tests).
  public get displayContent(): string {
    if (this.content === undefined) return '';

    if (typeof this.content === 'number') {
      if (this.content === 0 && !this.showZero) return '';
      if (this.content > this.max) return `${this.max}+`;
      return this.content.toString();
    }

    return this.content;
  }

  // Compute whether the badge should be shown from inputs immediately (getter used by template/tests).
  public get showBadge(): boolean {
    if (this.hidden) return false;
    if (this.content === undefined) return true;
    if (typeof this.content === 'number') {
      // Treat any non-zero number as a visible badge; showZero controls visibility of zero.
      return this.content !== 0 || this.showZero;
    }
    return typeof this.content === 'string' && this.content.length > 0;
  }
}

/**
 * Usage Examples:
 *
 * Standalone badge:
 * <ui-badge-glass content="New" variant="primary" [standalone]="true"></ui-badge-glass>
 *
 * Cart badge:
 * <ui-badge-glass [content]="cartCount" variant="error" position="top-right">
 *   <ui-icon name="shopping-cart"></ui-icon>
 * </ui-badge-glass>
 *
 * Notification badge:
 * <ui-badge-glass [content]="notificationCount" variant="primary" [pulse]="hasNewNotifications">
 *   <ui-icon name="bell"></ui-icon>
 * </ui-badge-glass>
 *
 * Status badge:
 * <ui-badge-glass content="Online" variant="success" size="sm" [standalone]="true"></ui-badge-glass>
 *
 * Different variants:
 * <ui-badge-glass content="5" variant="error">
 *   <ui-button-glass>Messages</button>
 * </ui-badge-glass>
 *
 * <ui-badge-glass content="Hot" variant="warning" size="xs" [standalone]="true"></ui-badge-glass>
 *
 * Large count with max:
 * <ui-badge-glass [content]="150" [max]="99" variant="primary">
 *   <ui-icon name="message-circle"></ui-icon>
 * </ui-badge-glass>
 *
 * E-commerce examples:
 *
 * Shopping cart:
 * <ui-badge-glass [content]="cartItemCount" variant="error" [pulse]="recentlyAdded">
 *   <ui-button-glass variant="ghost">
 *     <ui-icon name="shopping-cart"></ui-icon>
 *   </button>
 * </ui-badge-glass>
 *
 * Wishlist:
 * <ui-badge-glass [content]="wishlistCount" variant="primary">
 *   <ui-button-glass variant="ghost">
 *     <ui-icon name="heart"></ui-icon>
 *   </button>
 * </ui-badge-glass>
 *
 * Product status:
 * <ui-badge-glass content="Sale" variant="error" size="md" [standalone]="true"></ui-badge-glass>
 * <ui-badge-glass content="New Arrival" variant="success" [standalone]="true"></ui-badge-glass>
 * <ui-badge-glass content="Limited Edition" variant="premium" [standalone]="true"></ui-badge-glass>
 *
 * Stock status:
 * <ui-badge-glass content="Low Stock" variant="warning" [standalone]="true"></ui-badge-glass>
 * <ui-badge-glass content="Out of Stock" variant="error" [standalone]="true"></ui-badge-glass>
 *
 * User profile:
 * <ui-badge-glass content="Pro" variant="premium" position="bottom-right">
 *   <img src="avatar.jpg" class="w-10 h-10 rounded-full">
 * </ui-badge-glass>
 */
